<script setup lang="ts">
import { computed } from 'vue';

import AppRouterLink from '../appRouterLink/AppRouterLink.vue';
import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';
import type { CssCustomPropertyColor } from '../../helpers/cssCustomProperties';

import type { AppBackButtonType } from './AppBackButton.model';

export interface AppBackButtonProps {
  type: AppBackButtonType;
  link: string;
  text: string;
  font: 'font-label-l-bold' | 'font-label-l-light';
}

const props = defineProps<AppBackButtonProps>();

interface AppBackButtonEmits {
  (eventName: 'click'): void;
  (eventName: 'navigation-triggered'): void;
}

const emit = defineEmits<AppBackButtonEmits>();

const color = computed<CssCustomPropertyColor>(() =>
  props.type === 'black' ? 'color-black' : 'color-white'
);
</script>

<template>
  <button
    class="app-back-button"
    :class="{ [props.type]: true }"
    @click="emit('click')"
  >
    <AppRouterLink
      :link="props.link"
      :font="props.font"
      :color="color"
      @navigation-triggered="emit('navigation-triggered')"
    >
      <div class="go-back-link">
        <AppSvgIcon name="arrowLeft" size="24" />
        {{ props.text }}
      </div>
    </AppRouterLink>
  </button>
</template>

<style scoped lang="scss">
@import '../../styles/_media.scss';

.app-back-button {
  all: unset;
  padding: 0 var(--spacing-32);
  height: 100%;
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  transition: all var(--transition-short);

  @include for-less-or-equal-than-899-px {
    padding: 0;
  }

  @include for-less-or-equal-than-1200-px {
    padding: 0;
  }

  @include on-hover {
    cursor: pointer;
    background-color: var(--color-secondary-93);
  }

  &.white {
    color: var(--color-white);
  }

  &.black {
    color: var(--color-black);
  }

  .go-back-link {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-8);
  }
}
</style>
